/*
  Tabs.jsx
*/

// Npm.
import React, { useMemo } from 'react';

// Makes.
import { makeStyles, cx } from '../../makes/makeStyles';

// Styles.
const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    borderBottom: `1px solid ${theme.color_divider}`,
    whiteSpace: 'nowrap',
    overflow: 'auto',
    '& > .tab': {
      display: 'inline-flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: 'transparent',
      outline: 'none',
      border: 'none',
      margin: 0,
      borderRadius: 0,
      cursor: 'pointer',
      userSelect: 'none',
      appearance: 'none',
      verticalAlign: 'middle',
      textDecoration: 'none',
      fontWeight: 500,
      fontSize: '0.875rem',
      letterSpacing: '0.05em',
      textTransform: 'none',
      maxWidth: 360,
      minWidth: 90,
      minHeight: 48,
      flexShrink: 0,
      padding: theme.space(1.5, 2),
      overflow: 'hidden',
      textAlign: 'center',
      color: theme.color_text_primary,
      borderBottom: `2px solid transparent`,
      transition: 'border-bottom-color 0.1s ease',
      '&:focus-visible': {
        backgroundColor: theme.action_hover,
      },
      '&.selected': {
        borderBottomColor: theme.color_primary_main,
        color: theme.color_text_primary,
        '&:disabled': {
          borderBottomColor: theme.color_divider,
        }
      },
      '&:disabled': {
        color: theme.color_text_disabled,
        cursor: 'inherit',
      },
    }
  },
}));

/**
 * Renders a bunch of tabs.
 *
 * NOTE: Doesn't support horizontal scroll, as its not needed at the moment.
 */
export default function Tabs({
  
  // Tab props.
  value,
  options,
  onChange,
  disabled,

  // Other props.
  className,
  children,
  ...rest

}) {

  // Classes.

  const classes = useStyles();
  const rootClass = 'Tabs';
  const cls = useMemo(() => cx(rootClass, classes.root, className), [rootClass, classes.root, className]);

  // Props.

  const useValue = value || null;

  return (
    <div
      className={cls}
      role="tablist"
      {...rest}
    >

      {
        Boolean(options) && options.map(opt => {

          const useOptValue = opt.value || null;
          const selected = useValue === useOptValue;

          function handleClick(event) {
            if (onChange && !selected) {
              onChange(event, useOptValue);
            }
          }

          return (
            <button
              key={useOptValue}
              className={selected ? 'tab selected' : 'tab'}
              aria-selected={selected ? 'true' : 'false'}
              role="tab"
              type="button"
              onClick={handleClick}
              disabled={disabled}
            >
              { opt.label || null }
            </button>
          );
        })
      }

      { children }

    </div>
  );
}
